import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { QueryClient } from '@tanstack/angular-query-experimental';

import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

import { ToastService } from '@services/toast.service';
import { FeedApiService } from 'src/app/api/services/feed-api.service';
import {
  feedPostsListsFilter,
  injectSetFeedManualOrderMutation,
} from 'src/app/api/queries/feed.queries';
import { allWidgetDataQueriesFilter } from 'src/app/api/queries/widget.queries';

import * as feedActions from '../state/feed.actions';
import { getContentFilters } from '../state';
import { ContentFilters, Feed, Post, Widget } from '../models/core.models';
import { WidgetPersistenceService } from './widget-persistence.service';
import {
  FEED_MANUAL_ORDER_MAX_POSTS,
  FEED_SORT_FIELD_MANUAL,
  NomMediaChannel,
  SortDirection,
} from '../constants';

/**
 * The manual post order (WAP-1888): one owner for the write.
 *
 * Two callers save it - the results toolbar when the sort switches to Manual
 * on a feed without a saved order (a snapshot of the order the analyst was
 * looking at) and the arrange dialog - so the HTTP, the
 * `setFeedManualOrderSuccess` broadcast, the list and widget invalidations and
 * the toasts sit here, not in a hook per call site. The feed object is reducer
 * state, hence the broadcast instead of a cache patch.
 */
@Injectable({ providedIn: 'root' })
export class FeedManualOrderService {
  private readonly store = inject(Store);
  private readonly queryClient = inject(QueryClient);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly feedApi = inject(FeedApiService);
  private readonly setManualOrderMutation = injectSetFeedManualOrderMutation();
  private readonly widgetPersistence = inject(WidgetPersistenceService);

  /** Save `postIds` as the feed's manual order; true when the backend accepted it. */
  async save(feed: Feed, postIds: string[]): Promise<boolean> {
    try {
      const response = await this.setManualOrderMutation.mutateAsync({
        feedId: feed.id,
        postIds: postIds.slice(0, FEED_MANUAL_ORDER_MAX_POSTS),
      });

      this.store.dispatch(
        feedActions.setFeedManualOrderSuccess({
          manualOrder: response?.manual_order ?? [],
        }),
      );
      // * the order changes what the feed list and every widget over the feed answer
      this.queryClient.invalidateQueries(feedPostsListsFilter(feed.id));
      this.queryClient.invalidateQueries(allWidgetDataQueriesFilter());
      this.toast.showSuccess(
        this.translate.instant('feed-manual-order.message.manual-order-saved'),
      );

      return true;
    } catch {
      this.toast.showError(
        this.translate.instant(
          'feed-manual-order.message.saving-the-manual-order-failed',
        ),
      );

      return false;
    }
  }

  /**
   * Seed the manual order from the order the analyst sees: the feed under
   * `sortField` / `sortDirection` with the current period and filters, the
   * first `FEED_MANUAL_ORDER_MAX_POSTS` posts.
   */
  async snapshot(
    feed: Feed,
    sortField: string,
    sortDirection: SortDirection,
  ): Promise<boolean> {
    const posts = await this.fetchOrdered(feed, sortField, sortDirection);

    return this.save(
      feed,
      posts.map((post) => post.id),
    );
  }

  /**
   * The feed's posts under the saved manual order (saved ids first, the rest
   * newest first), current period and filters, up to the cap.
   */
  loadOrdered(feed: Feed): Promise<Post[]> {
    return this.fetchOrdered(feed, FEED_SORT_FIELD_MANUAL, 'desc');
  }

  /**
   * The posts a content widget shows, for arranging: the feed (any type, SEN-5941) narrowed by the
   * widget's attached filter dimensions, under its content filters (its custom
   * period and feed filters over the dashboard's), up to the cap. The widget's
   * own order comes first when it has one; without one the backend answers the
   * feed's order, which is what the widget falls back to.
   */
  async loadOrderedForWidget(widget: Widget, feedId: string): Promise<Post[]> {
    const own = widget.settings?.manual_order ?? [];
    const base = await this.widgetContentFilters(widget);
    return this.fetchPosts(feedId, {
      ...base,
      order: FEED_SORT_FIELD_MANUAL,
      sort: 'desc',
      ...(own.length > 0 ? { manual_order: own } : {}),
    });
  }

  /**
   * Save `postIds` as the widget's own manual order through the widget settings
   * PATCH (its owner invalidates the widget's data and toasts a failure); true
   * when the backend accepted it.
   */
  async saveForWidget(widget: Widget, postIds: string[]): Promise<boolean> {
    const saved = await this.widgetPersistence.applySettings([widget.id], {
      manual_order: postIds.slice(0, FEED_MANUAL_ORDER_MAX_POSTS),
    });
    if (saved) {
      this.toast.showSuccess(
        this.translate.instant('feed-manual-order.message.manual-order-saved'),
      );
    }
    return saved;
  }

  private async fetchOrdered(
    feed: Feed,
    order: string,
    sort: SortDirection,
  ): Promise<Post[]> {
    const current = await firstValueFrom(
      this.store.select(getContentFilters).pipe(take(1)),
    );
    return this.fetchPosts(feed.id, {
      ...current,
      order,
      sort,
      offset: 0,
      items_count: FEED_MANUAL_ORDER_MAX_POSTS,
    });
  }

  private async fetchPosts(
    feedId: string,
    contentFilters: ContentFilters,
  ): Promise<Post[]> {
    const response = await firstValueFrom(
      this.feedApi.getFeedPosts(feedId, contentFilters),
    );
    return response?.posts ?? [];
  }

  /**
   * The dashboard's content filters with the widget's custom period and feed
   * filters laid over them, as the widget data request is overlaid on the
   * backend. The widget's media channel "All" is the sentinel 0: the backend
   * overlay drops it as an integer, but sent as form data it reaches Solr as
   * a real `"0"` filter and empties the list, so it is dropped here.
   */
  private async widgetContentFilters(widget: Widget): Promise<ContentFilters> {
    const current = await firstValueFrom(
      this.store.select(getContentFilters).pipe(take(1)),
    );
    const own = widget.settings?.feed?.content_filters ?? {};
    const customPeriod =
      own.time_start && own.time_end
        ? { time_start: own.time_start, time_end: own.time_end }
        : {};
    const ownFilters = { ...(own.filters ?? {}) };
    const mediaChannels: unknown[] =
      ownFilters.post_source_media_channel_id?.include ?? [];
    if (mediaChannels.every((id) => Number(id) === NomMediaChannel.ALL)) {
      delete ownFilters.post_source_media_channel_id;
    }
    return {
      ...current,
      ...customPeriod,
      filters: { ...(current.filters ?? {}), ...ownFilters },
      offset: 0,
      items_count: FeedManualOrderService.widgetItemsCount(widget),
      dimension_id_filter: widget.settings?.dimension_id_filter ?? [],
    };
  }

  /**
   * The posts the widget shows: its "Number of items" setting, the cap when
   * the setting is empty or above it - the list to arrange is the list on the
   * card, nothing beyond it.
   */
  static widgetItemsCount(widget: Widget): number {
    const configured = Number(widget.settings?.number_of_items) || 0;
    return configured > 0
      ? Math.min(configured, FEED_MANUAL_ORDER_MAX_POSTS)
      : FEED_MANUAL_ORDER_MAX_POSTS;
  }
}
